/**
 * Centralized StoryDoodle+ messaging
 * Single source of truth for all premium feature descriptions and upgrade prompts
 */

export const STORYDOODLE_PLUS_MESSAGING = {
    // Main tagline and description
    tagline: "Unlock your creative potential with StoryDoodle+",

    // Core feature descriptions
    features: {
        quota: {
            short: "50 daily stories",
            long: "Create up to 50 stories per day (5x more than free users)",
            comparison: "50 daily stories vs 10 for free users",
        },
        ai: {
            short: "Premium AI",
            long: "Advanced AI model for richer storytelling and better drawing analysis",
            technical: "Premium AI model",
        },
        customization: {
            short: "Full customization",
            long: "Age range, reading level, genre selection & custom topics",
            list: ["Age range selection", "Reading level adjustment", "Genre selection", "Custom topics"],
        },
        pdf: {
            short: "Print & export",
            long: "Print preview and PDF export for your completed stories",
            feature: "Print preview for clean PDF exporting",
        },
    },

    // Upgrade prompt messages by urgency level
    upgradePrompts: {
        approaching:
            "Getting close to daily limits! Upgrade to StoryDoodle+ for 50 daily stories, premium AI, and print exports!",
        exceeded:
            "You've reached your daily limits. Upgrade to StoryDoodle+ for 50 daily stories, premium AI, and print exports!",
        promote: "Upgrade to StoryDoodle+ for 50 daily stories, premium AI, and print exports!",
        default: "Upgrade to StoryDoodle+ for 50 daily stories, premium AI, and print exports!",
    },

    // Trial-specific messaging
    trial: {
        description: "Experience all StoryDoodle+ features with no commitment required",
        duration: "7-Day StoryDoodle+ Trial",
        confirmDescription:
            "You're about to start your 7-day StoryDoodle+ trial. This is a one-time opportunity and cannot be reversed without contacting support. You'll get 50 daily stories, premium AI, genre selection, and print preview features.",
        active: "You're enjoying full StoryDoodle+ features during your trial period.",
        expired: "Your trial has expired. Upgrade to continue enjoying StoryDoodle+ features.",
        benefits: [
            "50 daily stories vs 10 for free users",
            "Premium AI model",
            "Age range, reading level, genre selection & custom topics",
            "Print preview for clean printing and PDF exporting",
        ],
    },

    // Pricing information
    pricing: {
        monthly: "$7/month",
        annual: "$70/year",
        full: "$7/month or $70/year",
    },

    // Feature summaries for different contexts
    summaries: {
        brief: "50 daily stories, premium AI, and print exports",
        detailed: "50 daily stories, premium AI model, full customization options, and print preview with PDF export",
        comparison: "vastly more stories & drawings, smarter AI, and print previews",
    },
} as const;

// Type exports for better TypeScript support
export type UpgradePromptType = keyof typeof STORYDOODLE_PLUS_MESSAGING.upgradePrompts;
export type FeatureType = keyof typeof STORYDOODLE_PLUS_MESSAGING.features;
